import { List } from "immutable";
import { uniqBy } from "lodash";
import { createSelector } from "reselect";

import { ContentOptions, ContentType } from "@seneca/course-service-types";

import { getAssignmentCourse } from "seneca-common/features/assignments/features/student-assignments/selectors/assignmentsCourse";
import {
  getAssignment,
  getAssignmentSectionIds,
  StudentAssignment,
  studentAssignmentSelectors
} from "seneca-common/features/assignments/features/student-assignments/state";
import { Assignment } from "seneca-common/features/assignments/state";
import {
  Contents,
  contentsSelectors,
  courseSelectors,
  getAllContents,
  getAllSections,
  getLeafSectionIds,
  SectionId,
  Sections,
  sectionsSelectors
} from "seneca-common/features/course/state";

type State = any;

export type SectionContent = {
  sectionId: string;
  contentType?: ContentType;
};

export type SectionInfo = SectionContent & {
  title: string;
  options?: ContentOptions;
};

export function makeGetAssignmentSectionContentsInCourseOrder(
  assignmentId: string
): (state: State) => List<SectionInfo> {
  const getCourseLeafSectionIds =
    makeGetAssignmentCourseLeafSectionIds(assignmentId);
  return createSelector(
    getCourseLeafSectionIds,
    (state: any) => getAssignmentSectionIds(state, assignmentId),
    state => getAssignment(state, assignmentId),
    getAllContents,
    getAllSections,
    getAssignmentSectionContentsInCourseOrder
  );
}

function getAssignmentSectionContentsInCourseOrder(
  orderedCourseSectionIds: List<SectionId>,
  assignmentSectionIds: List<SectionId>,
  assignment: StudentAssignment,
  contents: Contents,
  sections: Sections
): List<SectionInfo> {
  const orderedAssignmentSectionIds = getAssignmentSectionIdsInCourseOrder(
    orderedCourseSectionIds,
    assignmentSectionIds
  );

  return orderedAssignmentSectionIds
    .map(sectionId => {
      const contentIds = studentAssignmentSelectors.getContentIdsForSection(
        assignment as Assignment,
        sectionId
      );
      const title = sectionsSelectors.getSectionTitle(sections, sectionId);

      if (!contentIds || contentIds.size === 0) {
        return List([{ sectionId, title }]);
      }

      const contentTypesAndOptions = contentIds.map((contentId: string) => {
        const content = contentsSelectors.getContent(contents, contentId);
        return {
          contentType: content?.contentType,
          options: content?.options
        };
      });

      return uniqBy(
        [...contentTypesAndOptions],
        ({ contentType }) => contentType
      ).map(content => ({
        sectionId,
        ...content,
        title
      }));
    })
    .flatMap(ids => ids);
}

export function getAssignmentSectionIdsInCourseOrder(
  orderedCourseSectionIds: List<SectionId>,
  assignmentSectionIds: List<SectionId>
): List<SectionId> {
  return orderedCourseSectionIds.filter((sectionId: string) =>
    assignmentSectionIds.includes(sectionId)
  );
}

function makeGetAssignmentCourseLeafSectionIds(assignmentId: string) {
  return createSelector(
    (state: any) => getAssignmentCourse(state, assignmentId),
    getAllSections,
    getCourseLeafSectionIds
  );
}

export function getCourseLeafSectionIds(course: any, sections: any) {
  const courseSectionIds = courseSelectors.getCourseSectionIds(course);
  return getLeafSectionIds(sections, courseSectionIds);
}
